import { MobileLayout, LayoutType, BlockRef, MetaType } from "../types"
import {
  actionCreatorGuard,
  reducerGuard,
  processActionMeta,
  getArrayReducerGuard
} from "../reducers"

const kind = "seq_qti_screens"

export type SeqQtiScreen = {
  kind: typeof kind
  snapId: string
  version: number
  name: string
  layout: LayoutType
  isPublished: boolean
  mobileLayout: MobileLayout
  left: Array<BlockRef>
  right: Array<BlockRef>
  meta: MetaType
  exemplarSourceTest: string
  exemplarQuestion: string
  difficulty: number
  totalSeconds: number
  knowledgeNodeIds?: string[]
  standardIds?: string[]
  skillIds?: string[]
  tags?: string[]
  _updatedAt?: string
}

export const reducerAction = {
  snapId: actionCreatorGuard<SeqQtiScreen, string>("snapId", kind),
  meta: actionCreatorGuard<SeqQtiScreen, MetaType>("meta", kind),
  left: actionCreatorGuard<SeqQtiScreen, Array<BlockRef>>("left", kind),
  right: actionCreatorGuard<SeqQtiScreen, Array<BlockRef>>("right", kind),
  layout: actionCreatorGuard<SeqQtiScreen, LayoutType>("layout", kind),
  mobileLayout: actionCreatorGuard<SeqQtiScreen, MobileLayout>(
    "mobileLayout",
    kind
  ),
  isPublished: actionCreatorGuard<SeqQtiScreen, boolean>("isPublished", kind),
  name: actionCreatorGuard<SeqQtiScreen, string>("name", kind),
  exemplarSourceTest: actionCreatorGuard<SeqQtiScreen, string>(
    "exemplarSourceTest",
    kind
  ),
  exemplarQuestion: actionCreatorGuard<SeqQtiScreen, string>(
    "exemplarQuestion",
    kind
  ),
  difficulty: actionCreatorGuard<SeqQtiScreen, number>("difficulty", kind),
  totalSeconds: actionCreatorGuard<SeqQtiScreen, number>("totalSeconds", kind),
  knowledgeNodeIds: actionCreatorGuard<SeqQtiScreen, Array<string>>(
    "knowledgeNodeIds",
    kind
  ),
  standardIds: actionCreatorGuard<SeqQtiScreen, Array<string>>(
    "standardIds",
    kind
  ),
  skillIds: actionCreatorGuard<SeqQtiScreen, Array<string>>("skillIds", kind),
  tags: actionCreatorGuard<SeqQtiScreen, Array<string>>("tags", kind)
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in keyof Omit<
    SeqQtiScreen,
    "kind" | "version" | "_updatedAt"
  >]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const seqQtiScreenAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

export const seqQtiScreenReducer = reducerGuard<SeqQtiScreen>(kind)
  .case(...reducerAction.meta)
  .case(...reducerAction.left)
  .case(...reducerAction.right)
  .case(...reducerAction.layout)
  .case(...reducerAction.mobileLayout)
  .case(...reducerAction.name)
  .case(...reducerAction.difficulty)
  .case(...reducerAction.exemplarQuestion)
  .case(...reducerAction.exemplarSourceTest)
  .case(...reducerAction.knowledgeNodeIds)
  .case(...reducerAction.skillIds)
  .case(...reducerAction.standardIds)
  .case(...reducerAction.tags)
  .case(...reducerAction.totalSeconds)
  .default((prevState, action: any) => {
    action = processActionMeta(action)

    switch (action?.meta?.prop) {
      case "left":
        return getArrayReducerGuard<SeqQtiScreen, BlockRef>(
          kind,
          "left",
          "BlockRef",
          prevState,
          action
        )
      case "right":
        return getArrayReducerGuard<SeqQtiScreen, BlockRef>(
          kind,
          "right",
          "BlockRef",
          prevState,
          action
        )
    }

    return prevState
  })
